import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import type {SubtitlesDropdownListProps} from './subtitles-dropdown-list.types';
import styles from './subtitles-dropdown-list.module.scss';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AiSubtitleMenuOpenedFrom} from '@Libraries/ai-transcript.library';
import {useAppSelector} from '@/hooks';
import {getActivityIndicatorTypeForAiSubtitle} from '@Components/poster-editor/poster-editor-reducer';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {noop} from '@Utils/general.util';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getUserId, isUserPremium} from '@Libraries/user.library';

export function SubtitlesDropdownList({
  showBackButtonAsOptionItem = false,
  className = '',
  onBack = noop,
  onSelectAiSubtitlesOption = noop,
  onSelectManualSubtitlesOption = noop,
}: SubtitlesDropdownListProps): ReactElement {
  const activityIndicatorForAiSubtitle = useAppSelector(getActivityIndicatorTypeForAiSubtitle);
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());

  const getSubtitlesListOptions = (): ControlledListItemType[] => {
    return [
      {
        id: 'add-ai-subtitles',
        pill: !isPremium.current ? (
          <Icon className={`spacing-m-l-1 spacing-p-t-1 ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.SUCCESS} icon={'icon-crown'} />
        ) : undefined,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_ai_subtitles'),
        subText: window.i18next.t('pmwjs_ai_subtitles_option_description'),
        onClick: (id, e): void => {
          e?.preventDefault();
          e?.stopPropagation();
          trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_MENU, {[GA4EventParamName.MENU_OPENED_FROM]: AiSubtitleMenuOpenedFrom.ADD_TEXT_OPTION});
          onSelectAiSubtitlesOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: (
            <AddItemThumbnail icon="icon-ai-subtitles" className={styles.aiThumbnail} iconClassName={styles.aiIcon} activityIndicatorType={activityIndicatorForAiSubtitle} />
          ),
        },
      },
      {
        id: 'add-manual-subtitles',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_manual_subtitles'),
        subText: window.i18next.t('pmwjs_manual_subtitles_option_description'),
        onClick: (id, e): void => {
          e?.preventDefault();
          e?.stopPropagation();
          onSelectManualSubtitlesOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-captions" />,
        },
      },
    ];
  };

  return (
    <div key={'subtitle-options'}>
      {showBackButtonAsOptionItem ? (
        <Button
          size={Size.SMALL}
          type={Type.SECONDARY}
          onClick={(e): void => {
            e.preventDefault();
            e.stopPropagation();

            onBack();
          }}
          isFullWidth
          customClasses={`spacing-m-b-2`}
          text={window.i18next.t('pmwjs_back')}
          icon="icon-back"
          iconPlacement={IconPlacement.LEFT}
        />
      ) : null}
      <ControlledList className={`spacing-m-0 ${className}`} items={getSubtitlesListOptions()} />
    </div>
  );
}
